// Invoice detail: one bill sent to a customer, with its lines, payments,
// and history. Single-get shape (`FinanceInvoiceDetailResponse`) plus the
// invoice's payments list; there is no status-history table on the backend
// (status mutates in place), so the activity log is the audit trail for
// this invoice.

import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  type AuditEvent,
  createInvoicePortalToken,
  downloadInvoicePdf,
  getInvoiceDetail,
  getPeriodLock,
  type InvoiceDetail,
  type InvoicePayment,
  listAuditEvents,
  listChartAccounts,
  listPayments,
  portalInvoiceUrl,
} from "@/lib/api";
import { canCreditInvoice, invoiceListStatus } from "@/lib/worklist";
import {
  ApplyCreditDialog,
  ApplyPrepaymentDialog,
  RecordPaymentDialog,
  ReminderDialog,
} from "./InvoiceRowActions";
import { InvoicePosting } from "./InvoicePosting";
import { CreditNoteCreateDrawer } from "@/features/credit-notes/CreditNoteCreateDrawer";

function statusTone(label: string): StatusTone {
  switch (label) {
    case "Paid":
      return "success";
    case "Overdue":
      return "danger";
    case "Awaiting payment":
    case "Part paid":
      return "info";
    default:
      return "idle";
  }
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

type DrawerKind = "pay" | "send" | "credit" | "create-credit" | "prepay" | null;

export function InvoiceDetailPage() {
  const { invoiceId } = useParams({ strict: false }) as {
    invoiceId: string;
  };
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [payments, setPayments] = useState<InvoicePayment[] | null>(null);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [accountLabels, setAccountLabels] = useState<Map<string, string>>(
    new Map(),
  );
  const [lockDate, setLockDate] = useState<string | undefined>(undefined);
  const [postingLockDate, setPostingLockDate] = useState<string | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerKind>(null);

  const [portalBusy, setPortalBusy] = useState(false);
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadActivity = useCallback(() => {
    if (!invoiceId) return;
    setEventsError(null);
    listAuditEvents({ resource_id: invoiceId })
      .then((rows) => {
        setEvents(rows);
        setEventsError(null);
      })
      .catch((cause: unknown) => {
        setEvents(null);
        setEventsError(toErrorMessage(cause));
      });
  }, [invoiceId]);

  const reload = useCallback(() => {
    if (!invoiceId) return;
    setError(null);
    getInvoiceDetail(invoiceId)
      .then((loaded) => {
        setDetail(loaded);
        setPayments(loaded.payments);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
    listPayments({ invoice_id: invoiceId })
      .then(setPayments)
      .catch(() => {
        // The single-get already carries payments; a list failure here must
        // not blank the Payments tab.
      });
    loadActivity();
    listChartAccounts()
      .then((accounts) =>
        setAccountLabels(
          new Map(
            accounts.map((account) => [
              account.id,
              `${account.code} — ${account.name}`,
            ]),
          ),
        ),
      )
      .catch(() => setAccountLabels(new Map()));
    getPeriodLock()
      .then((lock) => {
        setLockDate(lock.lock_date ?? lock.advisor_lock_date);
        setPostingLockDate(
          [lock.lock_date, lock.advisor_lock_date]
            .filter((day): day is string => Boolean(day))
            .sort()
            .at(-1),
        );
      })
      .catch(() => {
        setLockDate(undefined);
        setPostingLockDate(undefined);
      });
  }, [invoiceId, loadActivity]);

  useEffect(reload, [reload]);

  const copyPortalLink = async () => {
    if (!invoiceId) return;
    setPortalBusy(true);
    setPortalLink(null);
    setError(null);
    try {
      const minted = await createInvoicePortalToken(invoiceId);
      const url = portalInvoiceUrl(minted.token);
      try {
        await navigator.clipboard.writeText(url);
        setPortalLink(`Customer link copied: ${url}`);
      } catch {
        setPortalLink(`Customer link (copy manually): ${url}`);
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setPortalBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!invoiceId) return;
    setPdfBusy(true);
    setError(null);
    try {
      await downloadInvoicePdf(invoiceId);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setPdfBusy(false);
    }
  };

  const paidCents = (payments ?? detail?.payments ?? []).reduce(
    (sum, payment) => sum + payment.amount_cents,
    0,
  );
  const totalCents = detail?.total_minor ?? 0;
  const remainingCents = Math.max(0, totalCents - paidCents);

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/invoices"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Invoices
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.invoice_number ?? invoiceId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.invoice_number}
            description={
              <>
                {detail.customer_name} · due{" "}
                {new Date(detail.due_at).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(invoiceListStatus(detail))}
                  label={invoiceListStatus(detail)}
                />
              </>
            }
            actions={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setDrawer("send")}
                >
                  Send reminder
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={copyPortalLink}
                  disabled={portalBusy}
                  title="Mint a token-scoped public link for this invoice and copy it"
                >
                  {portalBusy ? "Copying…" : "Copy customer link"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                >
                  {pdfBusy ? "Saving…" : "Download PDF"}
                </Button>
                {canCreditInvoice(detail) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setDrawer("credit")}
                  >
                    Apply credit
                  </Button>
                ) : null}
                {canCreditInvoice(detail) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setDrawer("create-credit")}
                  >
                    Credit note
                  </Button>
                ) : null}
                {canCreditInvoice(detail) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setDrawer("prepay")}
                  >
                    Apply prepayment
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setDrawer("pay")}
                >
                  Record payment
                </Button>
              </>
            }
          />

          {portalLink ? (
            <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm break-all text-[var(--success)]">
              {portalLink}
            </output>
          ) : null}

          <InvoicePosting
            invoice={detail}
            lockDate={postingLockDate}
            accountLabels={accountLabels}
            onInvoice={(updated) => {
              setDetail(updated);
              setPayments(updated.payments);
              loadActivity();
            }}
            onRefresh={reload}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: totalCents,
                  currency: detail.currency_code,
                })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Paid so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: paidCents,
                  currency: detail.currency_code,
                })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Still owed
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: remainingCents,
                  currency: detail.currency_code,
                })}
              </p>
            </div>
          </div>

          <section aria-label="Invoice lines" className="grid gap-2">
            <h2 className="text-lg font-semibold tracking-tight">Lines</h2>
              <div className="surface overflow-hidden rounded-2xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>What</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.lines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          This invoice has no lines.
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell className="text-xs">
                            {accountLabels.get(line.account_id) ?? (
                              <span
                                className="text-[var(--muted-foreground)]"
                                title={line.account_id}
                              >
                                Unknown account
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.tax_cents > 0 ? (
                              formatMoney({
                                cents: line.tax_cents,
                                currency: detail.currency_code,
                              })
                            ) : (
                              <span className="text-[var(--muted-foreground)]">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: line.amount_minor,
                              currency: detail.currency_code,
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
          </section>

          <section aria-label="Payments" className="grid gap-2">
            <h2 className="text-lg font-semibold tracking-tight">Payments</h2>
              <div className="surface overflow-hidden rounded-2xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Received</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments === null ? (
                      <TableRow>
                        <TableCell colSpan={4}>Loading…</TableCell>
                      </TableRow>
                    ) : payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          No payments recorded yet — use Record payment above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(payment.received_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{payment.method}</TableCell>
                          <TableCell>{payment.status}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: payment.amount_cents,
                              currency: detail.currency_code,
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
          </section>

          <details className="surface rounded-2xl p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Activity
            </summary>
              <div className="mt-3 overflow-hidden rounded-xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>What happened</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events === null && !eventsError ? (
                      <TableRow>
                        <TableCell colSpan={2}>Loading…</TableCell>
                      </TableRow>
                    ) : eventsError ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <span className="text-[var(--danger)]">
                            Could not load activity: {eventsError}
                          </span>
                          <div className="mt-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={loadActivity}
                            >
                              Retry
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : events && events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          No recorded activity for this invoice yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (events ?? []).map((event) => (
                        <TableRow key={event.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(event.occurred_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {event.action}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
          </details>
        </>
      ) : null}

      {drawer === "pay" && detail ? (
        <RecordPaymentDialog
          invoiceId={detail.id}
          currency={detail.currency_code}
          lockDate={lockDate}
          onClose={() => setDrawer(null)}
          onDone={reload}
        />
      ) : null}
      {drawer === "send" && detail ? (
        <ReminderDialog
          invoiceId={detail.id}
          customerName={detail.customer_name}
          onClose={() => setDrawer(null)}
        />
      ) : null}
      {drawer === "credit" && detail ? (
        <ApplyCreditDialog
          invoice={detail}
          onClose={() => setDrawer(null)}
          onDone={reload}
          onCreateCreditNote={() => setDrawer("create-credit")}
        />
      ) : null}
      {drawer === "create-credit" && detail ? (
        <CreditNoteCreateDrawer
          open
          initialInvoiceId={detail.id}
          onOpenChange={(open) => {
            if (!open) setDrawer(null);
          }}
          closeLabel="Back to invoice"
          onCreated={() => {
            setDrawer(null);
            reload();
          }}
        />
      ) : null}
      {drawer === "prepay" && detail ? (
        <ApplyPrepaymentDialog
          invoice={detail}
          currency={detail.currency_code}
          onClose={() => setDrawer(null)}
          onDone={reload}
        />
      ) : null}
    </div>
  );
}
